import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { studentApi } from '@/api';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  ItemCard,
  LoadingState,
  Modal,
  PageHeader,
  Select,
  Textarea,
  useToast,
} from '@/components/ui';
import { applyFieldErrors, errorMessage } from '@/hooks/useApiError';

import { experienceSchema } from '@/validations';
import { formatMonthYear } from '@/utils/format';

/**
 * The kinds of role. Full-time roles may not overlap each other (the API
 * refuses it); the rest may run alongside anything.
 */
const EMPLOYMENT_TYPES = [
  { value: 'FULL_TIME', label: 'Full-time' },
  { value: 'PART_TIME', label: 'Part-time' },
  { value: 'INTERNSHIP', label: 'Internship' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'FREELANCE', label: 'Freelance' },
  { value: 'VOLUNTEER', label: 'Volunteer' },
];

/** "PART_TIME" -> "Part time". */
function humanize(value) {
  const text = String(value).replace(/[_-]+/g, ' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** `embedded`: shown as a section of the profile page rather than on its own. */
export function StudentExperiencePage({ embedded = false }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['student-experience'],
    queryFn: studentApi.listExperience,
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['student-experience'] });
    void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
  }

  const remove = useMutation({
    mutationFn: (id) => studentApi.deleteExperience(id),
    onSuccess: () => {
      toast.success('Experience removed');
      setDeleting(null);
      invalidate();
    },
    onError: (mutationError) => toast.error(errorMessage(mutationError)),
  });

  if (isPending) return <LoadingState label="Loading your experience…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  return (
    <div>
      <PageHeader
        level={embedded ? 'h2' : 'h1'}
        title="Experience"
        description="Jobs, internships, volunteering and freelance work."
        action={
          <Button onClick={() => setCreating(true)}>
            <Icon name="plus" size={16} />
            Add experience
          </Button>
        }
      />

      {data.length === 0 ? (
        <EmptyState
          icon={<Icon name="internship" size={22} />}
          title="No experience added yet"
          description="Projects and part-time work count too."
          action={<Button onClick={() => setCreating(true)}>Add your first entry</Button>}
        />
      ) : (
        <ul className="space-y-3">
          {data.map((entry) => (
            <ItemCard
              key={entry.id}
              icon="internship"
              tone={entry.is_current ? 'success' : 'brand'}
              title={entry.title}
              subtitle={[entry.company_name, entry.location].filter(Boolean).join(' · ')}
              meta={`${formatMonthYear(entry.start_date)} – ${
                entry.is_current ? 'Present' : formatMonthYear(entry.end_date)
              }`}
              badges={
                <>
                  {entry.is_current && (
                    <Badge tone="success" dot>
                      Current
                    </Badge>
                  )}
                  {entry.employment_type && <Badge>{humanize(entry.employment_type)}</Badge>}
                </>
              }
              actions={
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    title={`Edit ${entry.title}`}
                    onClick={() => setEditing(entry)}
                  >
                    <Icon name="pencil" size={14} />
                    Edit
                  </Button>
                  <Button
                    variant="ghostDanger"
                    size="sm"
                    title={`Delete ${entry.title}`}
                    onClick={() => setDeleting(entry)}
                  >
                    <Icon name="trash" size={14} />
                    Delete
                  </Button>
                </>
              }
            >
              {entry.description}
            </ItemCard>
          ))}
        </ul>
      )}

      {/* Keyed per entry (and "new"), so the form starts from that entry's
          values -- or blank -- every time it opens, never from the last one. */}
      <ExperienceFormModal
        key={editing?.id ?? (creating ? 'new' : 'closed')}
        open={creating || editing !== null}
        entry={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={invalidate}
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete this experience?"
        message="This cannot be undone."
        confirmLabel="Delete"
        isLoading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
      />
    </div>
  );
}

/** What "Add experience" opens with: nothing carried over from an edit. */
const BLANK_EXPERIENCE = {
  company_name: '',
  title: '',
  employment_type: 'FULL_TIME',
  location: '',
  work_mode: '',
  start_date: '',
  end_date: '',
  is_current: false,
  description: '',
};

function ExperienceFormModal({ open, entry, onClose, onSaved }) {
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(experienceSchema),
    defaultValues: entry
      ? {
          company_name: entry.company_name,
          title: entry.title,
          employment_type: entry.employment_type ?? '',
          location: entry.location ?? '',
          work_mode: entry.work_mode ?? '',
          start_date: entry.start_date,
          end_date: entry.end_date ?? '',
          is_current: entry.is_current,
          description: entry.description ?? '',
        }
      : BLANK_EXPERIENCE,
  });
  const isCurrent = watch('is_current');

  async function onSubmit(values) {
    const payload = {
      company_name: values.company_name,
      title: values.title,
      employment_type: values.employment_type || null,
      location: values.location || null,
      work_mode: values.work_mode ?? null,
      start_date: values.start_date,
      end_date: values.end_date || null,
      is_current: values.is_current,
      description: values.description || null,
    };

    try {
      if (entry) await studentApi.updateExperience(entry.id, payload);
      else await studentApi.addExperience(payload);
      toast.success(entry ? 'Experience updated' : 'Experience added');
      reset();
      onSaved();
      onClose();
    } catch (error) {
      if (!applyFieldErrors(error, setError)) {
        toast.error(errorMessage(error, 'Could not save the entry'));
      }
    }
  }

  return (
    <Modal
      open={open}
      title={entry ? 'Edit experience' : 'Add experience'}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" form="experience-form" isLoading={isSubmitting}>
            Save
          </Button>
        </>
      }
    >
      <form id="experience-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Input label="Job title" required error={errors.title?.message} {...register('title')} />
        <Input
          label="Company"
          required
          error={errors.company_name?.message}
          {...register('company_name')}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Employment type"
            hint="Full-time roles can't overlap each other."
            {...register('employment_type')}
          >
            <option value="">Not specified (counts as full-time)</option>
            {EMPLOYMENT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
            {/* An older entry typed in by hand keeps its own wording. */}
            {entry?.employment_type &&
              !EMPLOYMENT_TYPES.some((type) => type.value === entry.employment_type) && (
                <option value={entry.employment_type}>{entry.employment_type}</option>
              )}
          </Select>
          <Select label="Work mode" {...register('work_mode')}>
            <option value="">Not specified</option>
            <option value="ONSITE">Onsite</option>
            <option value="REMOTE">Remote</option>
            <option value="HYBRID">Hybrid</option>
          </Select>
        </div>
        <Input label="Location" {...register('location')} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Start date"
            type="date"
            required
            error={errors.start_date?.message}
            {...register('start_date')}
          />
          <Input
            label="End date"
            type="date"
            required={!isCurrent}
            disabled={isCurrent}
            hint={isCurrent ? 'Not needed while you work here.' : undefined}
            error={errors.end_date?.message}
            {...register('end_date')}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="border-line text-brand-600 size-4 rounded border"
            {...register('is_current', {
              // A current role has no end date; clear it rather than refuse.
              onChange: (event) => {
                if (event.target.checked) setValue('end_date', '', { shouldValidate: true });
              },
            })}
          />
          <span className="text-ink-muted">I currently work here</span>
        </label>
        <Textarea
          label="What did you do?"
          error={errors.description?.message}
          {...register('description')}
        />
      </form>
    </Modal>
  );
}
